import {
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { ampli } from 'lib-shared-tracking';
import { PureComponent } from 'react';
import { Field, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';

import { isNumberValidWithReason } from 'app/utils/telephonyHelpers';
import type { TelephonyState } from 'components/settings/reducers/sections/telephony';

import styles from './externalCallerIdModal.module.scss';

import '../telephony.less';

interface Verification {
  phone_number: string;
  code: string;
  name?: string;
}

interface FormValues {
  phone_number: string;
  name: string | null;
  extension: string | null;
}

interface Props {
  isOpen: boolean;
  verifyPhoneNumber: Verification;
  verifyExternalCallerId: (payload: FormValues) => Promise<void>;
  onClose: VoidFunction;
  ui: TelephonyState['ui'];
}

interface State extends Omit<FormValues, 'name'> {
  numberError: null;
}

const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values) {
    return errors;
  }

  if (values.phone_number) {
    if (
      typeof values.phone_number === 'string' &&
      values.phone_number.trim().length > 0
    ) {
      const validateNumberResult = isNumberValidWithReason({
        phoneNumber: values.phone_number,
      });
      if (validateNumberResult !== true) {
        console.warn('validateNumberResult', validateNumberResult);
        errors.phone_number = validateNumberResult;
      }
    }
  } else {
    errors.phone_number = 'Phone number is required.';
  }

  if (!values.name) {
    errors.name = 'Name is required.';
  }

  return errors;
};

class ExternalCallerIdModalImpl extends PureComponent<
  Props & InjectedFormProps<FormValues, Props>,
  State
> {
  state: State = {
    phone_number: '',
    extension: '',
    numberError: null,
  };

  componentDidUpdate(prevProps: Props) {
    const {
      isOpen: prevIsOpen,
      ui: { externalCallerIdModalShown: prevExternalCallerIdModalShown },
    } = prevProps;
    const {
      isOpen,
      ui: { externalCallerIdModalShown },
    } = this.props;

    if (prevIsOpen && !isOpen) {
      this.setState({
        phone_number: '',
        extension: '',
        numberError: null,
      });
    }

    if (
      prevExternalCallerIdModalShown === true &&
      externalCallerIdModalShown === false
    ) {
      this.closeModal();
    }
  }

  @boundMethod
  handleFormSubmit(values: FormValues) {
    ampli.phoneAddOutboundCallerIdFormSubmitted();

    const { verifyExternalCallerId } = this.props;
    const { extension, name, phone_number } = values;

    const payload: FormValues = {
      phone_number,
      extension: extension || null,
      name: name || null,
    };

    verifyExternalCallerId(payload).catch(() => {
      /** Noop. */
    });
  }

  @boundMethod
  closeModal() {
    ampli.phoneAddOutboundCallerIdFormCancelled();

    const { onClose } = this.props;

    this.setState({
      phone_number: '',
      extension: '',
      numberError: null,
    });

    onClose();
  }

  @boundMethod
  verificationMessage(verifyPhoneNumber: Verification) {
    const { code, phone_number } = verifyPhoneNumber;
    const isWithCode = !!code;

    if (isWithCode) {
      return (
        <div className={styles.verification}>
          <h4>Verify {phone_number}</h4>
          <Text.Paragraph>
            You’ll receive an automatic phone call within the next minute.
            Please enter the code below during the call to verify your phone
            number:
          </Text.Paragraph>
          <h3>
            <b>{code}</b>
          </h3>
        </div>
      );
    }

    return (
      <div className={styles.verification}>
        <h4>Verifying {phone_number}</h4>
        <Text.Paragraph>
          This number is being automatically verified. You will receive a
          notification, once the verification is completed.
        </Text.Paragraph>
      </div>
    );
  }

  render() {
    const { isOpen, verifyPhoneNumber, ui, handleSubmit } = this.props;
    const isVerifyNumberShown = !!ui.verifyPhoneNumberShown;
    const isLoading = status == 'loading';

    return (
      <form onSubmit={handleSubmit(this.handleFormSubmit)}>
        <Modal isOpen={isOpen} onClose={this.closeModal}>
          <ModalHead onClose={this.closeModal} title="Add external caller ID" />
          <ModalBody>
            <div className="external-caller-id-modal__body">
              {isVerifyNumberShown ? (
                this.verificationMessage(verifyPhoneNumber)
              ) : (
                <>
                  <Field
                    label="Phone number"
                    name="phone_number"
                    className="external-caller-id-modal__phone"
                    component={FieldText}
                    placeholder="e.g. +4500000000"
                    type="text"
                    helpIcon={{
                      title: 'Phone number',
                      text: 'Add a phone number you’d like to add to your caller ID list. Your organization can use this number for making outbound calls.',
                    }}
                    required
                  />
                  <Field
                    label="Name"
                    name="name"
                    className="external-caller-id-modal__name"
                    component={FieldText}
                    placeholder="e.g. Customer Support"
                    type="text"
                    helpIcon={{
                      title: 'Name',
                      text: 'Specify the name you’d like to associate with this caller ID (e.g. Customer Support, complaints, high-priority, etc.).',
                    }}
                    required
                  />
                  <Field
                    label="Extension"
                    name="extension"
                    className="external-caller-id-modal__extension"
                    component={FieldText}
                    placeholder="e.g. 1, 5, *, #"
                    type="text"
                    helpIcon={{
                      title: 'Extension',
                      text: 'Specify any extensions or keypad selection to route calls through an IVR menu for this phone number.',
                    }}
                  />
                </>
              )}
            </div>
          </ModalBody>
          <ModalFoot
            {...(!isVerifyNumberShown && {
              cancelOnClick: this.closeModal,
            })}
            confirmName="Verify"
            confirmOnClick={handleSubmit(this.handleFormSubmit)}
            confirmProps={{
              disabled: isLoading,
              type: 'submit',
              ...(isVerifyNumberShown && {
                isLoading: true,
                loadingIndicator: <Spinner size={16} />,
              }),
            }}
          />
        </Modal>
      </form>
    );
  }
}

export const ExternalCallerIdModal = reduxForm<FormValues, Props>({
  form: 'ExternalCallerIdModal',
  enableReinitialize: true,
  validate,
})(ExternalCallerIdModalImpl);
